"use client";

import {
  useEffect,
  useState,
  type MouseEvent,
  type PointerEvent,
} from "react";

const HOLD_DELAY_MS = 400;
const REPEAT_START_MS = 160;
const REPEAT_FASTEST_MS = 40;
const REPEATS_BEFORE_SPEEDUP = 6;
const SPEEDUP_FACTOR = 0.7;

interface HoldRepeatInput {
  onTick: () => boolean;
  onRelease: () => void;
  onCancel: () => void;
}

function nextRepeatDelay(delay: number, repeats: number): number {
  return repeats >= REPEATS_BEFORE_SPEEDUP
    ? Math.max(REPEAT_FASTEST_MS, delay * SPEEDUP_FACTOR)
    : REPEAT_START_MS;
}

function createHoldMachine(initial: HoldRepeatInput) {
  let latest = initial;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let holding = false;
  let repeated = false;

  const dispose = () => {
    clearTimeout(timer);
    window.removeEventListener("pointerup", stop);
  };
  const end = (cancelled: boolean) => {
    dispose();
    if (!holding) return;
    holding = false;
    if (cancelled && !repeated) latest.onCancel();
    else latest.onRelease();
  };
  const stop = () => end(false);
  const cancel = () => end(true);
  const schedule = (delay: number, repeats: number) => {
    timer = setTimeout(() => {
      repeated = true;
      if (!latest.onTick()) {
        stop();
        return;
      }
      schedule(nextRepeatDelay(delay, repeats), repeats + 1);
    }, delay);
  };
  const start = () => {
    if (holding) return;
    holding = true;
    repeated = false;
    window.addEventListener("pointerup", stop);
    if (!latest.onTick()) {
      stop();
      return;
    }
    schedule(HOLD_DELAY_MS, 0);
  };

  const setInput = (input: HoldRepeatInput) => {
    latest = input;
  };

  return { start, stop, cancel, dispose, setInput };
}

export function useHoldRepeat(input: HoldRepeatInput) {
  const [machine] = useState(() => createHoldMachine(input));
  useEffect(() => {
    machine.setInput(input);
  });
  useEffect(() => machine.dispose, [machine]);

  return {
    onPointerDown: (event: PointerEvent<HTMLButtonElement>) => {
      if (event.button === 0) machine.start();
    },
    onPointerUp: machine.stop,
    onPointerLeave: machine.stop,
    onPointerCancel: machine.cancel,
    onClick: (event: MouseEvent<HTMLButtonElement>) => {
      if (event.detail !== 0) return;
      machine.start();
      machine.stop();
    },
    onContextMenu: (event: MouseEvent<HTMLButtonElement>) =>
      event.preventDefault(),
  };
}
